import { useMemo } from 'react';
import { useInfinitePredefinedScreenersQuery } from '@/hooks/useInfiniteCompanyScreenersQuery';
import { companyGqlMapper } from '@/hooks/useInfiniteCompanyScreenersQuery/utils';
import type { AdditionalScreenerFilter } from '@/hooks/useInfiniteCompanyScreenersQuery/types';

type PredefinedScreenerQueryParams = {
  gridViewId: number;
  pageSize: number;
  initPage?: number;
  isInfinite?: boolean;
  additionalFilters: AdditionalScreenerFilter[];
  returnRecentCompaniesOnly?: boolean;
};

export const usePredefinedScreenersQuery = ({
  pageSize,
  initPage = 0,
  gridViewId,
  isInfinite,
  additionalFilters,
  returnRecentCompaniesOnly,
}: PredefinedScreenerQueryParams) => {
  const {
    data: dataGQL,
    fetchNextPage: fetchNextPageGQL,
    hasNextPage: hasNextPageGQL,
    isFetching: isFetchingCompaniesGQL,
    isFetched: isFetchedGQL,
    isError: isErrorGQL,
    status: statusGQL,
  } = useInfinitePredefinedScreenersQuery({
    gridViewId,
    limit: pageSize,
    initPage,
    additionalFilters,
    displayRecentlyAddedCompanies: true,
    returnRecentCompaniesOnly,
  });

  const enhancedFetchNextPageGQL = useMemo(() => {
    const shouldFetchNextPage =
      !isErrorGQL && isInfinite && !isFetchingCompaniesGQL;
    return shouldFetchNextPage ? fetchNextPageGQL : () => {};
  }, [fetchNextPageGQL, isErrorGQL, isFetchingCompaniesGQL, isInfinite]);

  const firstPageGQL = dataGQL?.pages[0];
  const totalRecordsGQL = firstPageGQL?.totalHits ?? 0;
  const companiesGQL = useMemo(() => {
    const flattenCompanies =
      dataGQL?.pages
        .map((page) => page.companies)
        .flat()
        .filter(Boolean) || [];
    return flattenCompanies.map(companyGqlMapper);
  }, [dataGQL]);
  const totalPagesGQL = Math.ceil(totalRecordsGQL / pageSize);
  const systemRestrictions = firstPageGQL?.meta?.systemRestrictions;

  return {
    companies: companiesGQL,
    fetchNextPage: enhancedFetchNextPageGQL,
    hasNextPage: hasNextPageGQL,
    isFetching: isFetchingCompaniesGQL,
    isFetched: isFetchedGQL,
    totalRecords: totalRecordsGQL,
    totalPages: totalPagesGQL,
    newCompaniesCount: firstPageGQL?.newCompaniesTotalHits ?? null,
    systemRestrictions: systemRestrictions ?? null,
    status: statusGQL,
  };
};
